import { useMemo, useState } from "react";
import { Box, Dialog, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import type { RootState } from "../../../app/rootReducer";
import { useBackgroundImage } from "../../../components/FacilityManager/Hooks/useBackgroundImage";
import { DisplayViewport } from "../../../components/FacilityManager/Components/DisplayViewport/DisplayViewport";
import { FloorPlanBackground } from "../../../components/FacilityManager/Components/FloorPlanBackground/FloorPlanBackground";
import { PlaceContainer } from "../../../components/FacilityManager/Components/Place/PlaceContainer";
import { ZoneContainer } from "../../../components/FacilityManager/Components/Zone/ZoneContainer";
import { EmptyBackgroundText } from "../../../components/FacilityManager/Components/Canvas/Partials/EmptyBackgroundText.partial";
import { PlanViewMode } from "../../../components/FacilityManager/Components/PixiViewport/PixiViewport";
import type { IViewport } from "../../../components/FacilityManager/Domain/Types/FloorPlan/Viewport.type";
import {
  generateBookingPlaceVariant,
  generateZoneVariant
} from "../../../components/FacilityManager/Functions/generateVariant";
import {
  getFloorPlanCanvasCorner,
  resolveFloorPlanCanvasSize,
  resolveFloorPlanPlaceScale
} from "../../../components/FacilityManager/Functions/floorPlanCanvas.functions";
import { useGeometry } from "../../../components/FacilityManager/Hooks/useGeometry";
import { useMousePosition } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/useMousePosition";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import type { BookingInputs } from "../../../components/FacilityManager/Domain/Types/BookingInputs.type";
import { SelectionPrompt2 } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/SelectionPrompt(2)";
import { clickableByPlaceType } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/Functions/SelectFloorPlanView.functions";
import {
  buildColleagueDeskTooltipLines,
  buildColleagueWorkplaceSeatStatus,
  colleagueFloorSceneKey,
  generateColleagueFloorIcon,
  isColleagueUserOccupiedDesk,
  occupancyBookingsForDesk,
  resolveColleagueDeskClickKind,
  toDrawableColleagueFloorIcon,
  toHighlightedColleagueUserBookings
} from "./colleague-workplace-floor.functions";
import { ColleagueDeskClickKind } from "./colleague-workplace-floor.types";
import { ColleagueWorkplaceFloorDeskTooltip } from "./colleague-workplace-floor-desk-tooltip.partial";

type P = {
  floorPlan?: IFloorPayload;
  currentFloor?: FloorAvailability;
  isLoading: boolean;
  highlightInventoryId?: number;
  searchedColleagues: ConnectedBookingData[];
  bookingInputs: BookingInputs;
  timeFormat: string;
  onConfirmInventoryId: (inventoryId: number) => void;
  onOpenSessionTimeEditor: () => void;
  onOccupancyInventoryId: (inventoryId: number) => void;
};

export function ColleagueWorkplaceFloorCanvas({
  floorPlan,
  currentFloor,
  isLoading,
  highlightInventoryId,
  searchedColleagues,
  bookingInputs,
  timeFormat,
  onConfirmInventoryId,
  onOpenSessionTimeEditor,
  onOccupancyInventoryId
}: Readonly<P>) {
  const { t } = useTranslation();
  const { userInformation } = useSelector((state: RootState) => state.login);
  const { convertPixiPoints } = useGeometry();
  const [mouseX, mouseY] = useMousePosition();
  const [viewportDimensions, setViewportDimensions] = useState<IViewport>({ width: 0, height: 0 });
  const [activeTouchCount, setActiveTouchCount] = useState(0);
  const [selectedPlace, setSelectedPlace] = useState<number | undefined>();
  const [hoveredInventoryId, setHoveredInventoryId] = useState<number | undefined>();
  const sceneKey = colleagueFloorSceneKey(floorPlan);
  const [appliedSceneKey, setAppliedSceneKey] = useState(sceneKey);

  const { background, isEmptyBgUsed, unsetBackgroundImage } = useBackgroundImage({
    backgroundImageUrl: floorPlan?.outlineUrl,
    viewport: floorPlan?.viewport
  });

  if (sceneKey !== appliedSceneKey) {
    setAppliedSceneKey(sceneKey);
    unsetBackgroundImage();
    setSelectedPlace(undefined);
    setHoveredInventoryId(undefined);
  }

  const canvasSize = useMemo(() => {
    if (!background || !floorPlan) {
      return undefined;
    }
    return resolveFloorPlanCanvasSize(floorPlan, background);
  }, [background, floorPlan]);
  const placeScale = useMemo(() => {
    if (!background || !floorPlan) {
      return 0;
    }
    return resolveFloorPlanPlaceScale(floorPlan, background);
  }, [background, floorPlan]);

  const seatStatus = useMemo(
    () =>
      currentFloor
        ? buildColleagueWorkplaceSeatStatus(currentFloor)
        : {
            availableList: [],
            occupiedList: [],
            disableList: [],
            restrictedList: [],
            userOccupiedList: [],
            partialList: []
          },
    [currentFloor]
  );

  const searchedIds = useMemo(
    () => new Set(searchedColleagues.map(row => row.bookingInventoryId)),
    [searchedColleagues]
  );
  const highlightedUserBookings = useMemo(
    () => toHighlightedColleagueUserBookings(bookingInputs.usersBookedFor, highlightInventoryId),
    [bookingInputs.usersBookedFor, highlightInventoryId]
  );
  const selectedPlaceEntry = useMemo(
    () => floorPlan?.places.find(place => place.id === selectedPlace),
    [floorPlan?.places, selectedPlace]
  );
  const currentUserForOccupancy = useMemo(
    () => ({
      userId: userInformation.sub,
      firstName: userInformation.firstName,
      lastName: userInformation.surname,
      email: userInformation.email
    }),
    [userInformation.email, userInformation.firstName, userInformation.sub, userInformation.surname]
  );
  const hoverTooltipLines = useMemo(() => {
    if (hoveredInventoryId == null || selectedPlace != null) {
      return [];
    }
    const bookings = occupancyBookingsForDesk({
      inventoryId: hoveredInventoryId,
      connectedBookingData: currentFloor?.connectedBookingData ?? [],
      currentUser: currentUserForOccupancy,
      isUserOccupied: isColleagueUserOccupiedDesk(hoveredInventoryId, seatStatus)
    });
    return buildColleagueDeskTooltipLines(
      bookings,
      timeFormat,
      t("_colleagueFloor_bookingTimeUnknown")
    );
  }, [
    currentFloor?.connectedBookingData,
    currentUserForOccupancy,
    hoveredInventoryId,
    seatStatus,
    selectedPlace,
    t,
    timeFormat
  ]);
  const isViewportReady = Boolean(
    floorPlan && currentFloor && background && placeScale && canvasSize
  );
  const waitingForOutline = Boolean(floorPlan?.outlineUrl) && !isViewportReady;

  if (isLoading || waitingForOutline) {
    return (
      <Skeleton
        variant="rectangular"
        data-testid="colleague-workplace-floor-loading"
        sx={{ width: "100%", height: "100%", minHeight: 0 }}
      />
    );
  }

  if (!floorPlan || !currentFloor || !background || !placeScale || !canvasSize) {
    return (
      <Box
        data-testid="colleague-workplace-floor-empty"
        sx={{ width: "100%", height: "100%", minHeight: 0 }}
      />
    );
  }

  return (
    <Box
      data-testid="colleague-workplace-floor-plan"
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        flex: 1,
        position: "relative",
        overflow: "hidden"
      }}
    >
      <DisplayViewport
        key={sceneKey}
        planViewMode={PlanViewMode.BOOKING}
        floorPlanName="colleague-workplace"
        initialZoomEnd={getFloorPlanCanvasCorner(canvasSize)}
        currentFloorPlan={floorPlan}
        placeScale={placeScale}
        viewportDimensions={viewportDimensions}
        setViewportDimensions={setViewportDimensions}
        setActiveTouchCount={setActiveTouchCount}
      >
        {!isEmptyBgUsed && <FloorPlanBackground texture={background} floorPlan={floorPlan} />}
        {isEmptyBgUsed && (
          <EmptyBackgroundText
            rectWidth={floorPlan.viewport.width}
            rectHeight={floorPlan.viewport.height}
            posY={5}
          />
        )}
        {floorPlan.zones.map(zone => (
          <ZoneContainer
            id={zone.id}
            key={zone.id}
            inventoryId={zone.inventory?.id}
            walls={convertPixiPoints(zone.coordinates)}
            disabled={zone.disabled}
            zoneType={zone.inventory?.zoneTypeId ?? 0}
            onClick={() => undefined}
            isClickable={false}
            isSelected={false}
            showWarning={false}
            interactiveConferenceWarning={false}
            setIsHoverWarning={() => undefined}
            activeTouchCount={activeTouchCount}
            warnScale={1}
            variant={generateZoneVariant(zone, seatStatus)}
          />
        ))}
        {floorPlan.places.map(workplace => {
          const inventoryId = workplace.inventoryId || workplace.inventory?.id;
          const placeTypeId = workplace.placeTypeId || workplace.inventory?.placeTypeId;
          const isWorkplace = clickableByPlaceType(BookingType.WORKPLACE, placeTypeId);
          const clickKind = isWorkplace
            ? resolveColleagueDeskClickKind(inventoryId, seatStatus)
            : ColleagueDeskClickKind.NONE;
          const isClickable = clickKind !== ColleagueDeskClickKind.NONE;
          const isColleaguesPlace = inventoryId != null && searchedIds.has(inventoryId);
          const isUserOccupiedPlace = isColleagueUserOccupiedDesk(inventoryId, seatStatus);
          const deskBookings =
            isWorkplace && inventoryId != null
              ? occupancyBookingsForDesk({
                  inventoryId,
                  connectedBookingData: currentFloor.connectedBookingData ?? [],
                  currentUser: currentUserForOccupancy,
                  isUserOccupied: isUserOccupiedPlace
                })
              : [];
          const hasOccupancyTooltip = deskBookings.length > 0;
          const acceptsPointer = isClickable || hasOccupancyTooltip;
          return (
            <PlaceContainer
              key={workplace.id}
              boundingBox={{
                height: workplace.inventory?.boundingBox?.height ?? 80,
                width: workplace.inventory?.boundingBox?.width ?? 160
              }}
              inventoryId={inventoryId}
              id={workplace.id}
              position={workplace.position}
              rotation={workplace.rotate}
              isHighlighted={inventoryId === highlightInventoryId}
              isFavorite={false}
              isSelected={workplace.id === selectedPlace}
              onClick={() => {
                if (!isClickable || inventoryId == null) {
                  return;
                }
                if (clickKind === ColleagueDeskClickKind.BOOK) {
                  setSelectedPlace(workplace.id);
                  return;
                }
                onOccupancyInventoryId(inventoryId);
              }}
              onPointerEnter={() => {
                if (hasOccupancyTooltip && inventoryId != null) {
                  setHoveredInventoryId(inventoryId);
                }
              }}
              onPointerLeave={() => {
                setHoveredInventoryId(prev => (prev === inventoryId ? undefined : prev));
              }}
              isSelectable={isClickable}
              eventMode={acceptsPointer ? "static" : "none"}
              variant={generateBookingPlaceVariant(
                { workplace, zone: undefined },
                seatStatus,
                BookingType.WORKPLACE
              )}
              icon={toDrawableColleagueFloorIcon(
                generateColleagueFloorIcon({
                  highlightedUserBookings,
                  userInformation,
                  searchedColleagues,
                  inventoryId,
                  isUserOccupiedPlace
                }),
                userInformation
              )}
              isColleaguesPlace={isColleaguesPlace}
              placeTypeId={placeTypeId}
              placeScale={placeScale}
              activeTouchCount={activeTouchCount}
              tool="SELECT.BOOK"
            />
          );
        })}
      </DisplayViewport>
      <ColleagueWorkplaceFloorDeskTooltip
        lines={hoverTooltipLines}
        mouseX={mouseX}
        mouseY={mouseY}
      />
      {selectedPlaceEntry && (
        <Dialog open onClose={() => setSelectedPlace(undefined)}>
          <SelectionPrompt2
            selected={{ place: selectedPlaceEntry, zone: undefined }}
            selectableByVariant={true}
            bookingInputs={bookingInputs}
            floorPlanForService={undefined}
            floorInventoryId={floorPlan.floorInventoryId}
            userIndex={0}
            userId={userInformation.sub}
            isOccupiedDataLoading={false}
            onConfirm={() => {
              const inventoryId =
                selectedPlaceEntry.inventoryId ?? selectedPlaceEntry.inventory?.id;
              if (inventoryId != null) {
                onConfirmInventoryId(inventoryId);
              }
              setSelectedPlace(undefined);
            }}
            onClose={event => {
              event.stopPropagation();
              setSelectedPlace(undefined);
            }}
            confirmButtonLabel={t("_colleagueFloor_confirmBook")}
            openTimeDialog={() => {
              setSelectedPlace(undefined);
              onOpenSessionTimeEditor();
            }}
          />
        </Dialog>
      )}
    </Box>
  );
}
